<script>
    import _ from 'lodash'
    import $ from '../shared/helper'
    import { CONST, DEFAULT_CALLBACKS, FORM_PROPERTIES, TOOLBAR_OPTIONS } from '../shared/constants'
    import { ATYPES, MTYPES } from '../state/types'
    import { SPEECH } from '../shared/speech'
    import GF from '../gfclass'
    import VGridformFieldList from './v-gridform-field-list'

    export default {
        name: 'VGridform',
        components: {VGridformFieldList},
        props: {
            value: {
                type: Boolean
            },
            name: {
                type: String,
                required: true
            },
            config: {
                type: Object,
                required: false,
                default: () => _.cloneDeep(FORM_PROPERTIES)
            },
            callbacks: {
                type: Object,
                default: () => null
            },
            context: {
                type: Object,
                default: () => null
            },
            devTools: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                isActive: false,
                valid: false,
                drawer: false,
                changes: false,
                submitting: false,
                actiontext: 'OK',
                unsubscribeStore: null,
                unwatchFormData: null
            }
        },

        computed: {
            containerClasses() {
                const options = this.options || {}
                let cls = options.cls ? options.cls + ' ' : ''
                if (options.fluid) cls += 'container--fluid '
                if (options.padding) cls += options.padding + ' '
                if (options.fillHeight) cls += 'fill-height '
                return `vgf__container ${cls}`.trim()
            },
            layoutClasses() {
                const options = this.options || {}
                let cls = ''
                if (options.center) cls += 'justify-center align-center '
                if (options.fillHeight) cls += 'fill-height '
                return `${cls}`.trim()
            },
            mainClasses() {
                const options = this.options || {}
                const md = options.size || 12
                let cls = ''
                if (options.fillHeight) cls += 'fill-height '
                return `pa-0 xs12 sm12 md${md} ${cls}`.trim()
            },
            sheetClasses() {
                const options = this.options || {}
                let cls = options.cls ? options.cls + ' ' : ''
                // if (options.padding) cls += options.padding + ' '
                if (options.fillHeight) cls += 'fill-height '
                return `vgf ${cls}`.trim()
            },
            contentClasses() {
                const cls = this.options.contentClass || ''
                return `vgf__content ${cls}`.trim()
            },

            // TODO rename to config
            options() {
                return this.$store.getters[$.ns('config', this.name)]
            },
            dark() {
                return _.get(this.options, 'dark', false)
            },
            drawerOptions() {
                return {
                    dark: !this.dark,
                    light: this.dark,
                    absolute: true,
                    temporay: true, // NOTE doesn't work
                    disableResizeWatcher: true
                }
            },
            toolbarOptions() {
                const options = this.options || {}
                const toolbar = _.pick(options, _.keys(TOOLBAR_OPTIONS))
                toolbar.bind = {
                    absolute: toolbar.toolbarAbsolute,
                    color: toolbar.toolbarColor
                }
                return toolbar
            },
            actionOptions() {
                const {block = true, raised = true, text = false, color = 'primary'} = _.get(this.options, 'actionOptions') || {}
                return {block, raised, text, color}
            },

            customCallbacks() {
                return {
                    ...DEFAULT_CALLBACKS,
                    ...this.callbacks
                }
            },

            fieldsKeys() {
                return this.$store.getters[$.ns('fieldsKeys', this.name)]
            },
            rows() {
                return this.$store.getters[$.ns('rows', this.name)]
            },
            fieldLists: {
                get() {
                    return this.$store.getters[$.ns('fieldLists', this.name)]
                },
                set(fls) {
                    let rows = []
                    _.each(fls, fl => {rows = rows.concat(fl.rows)})
                    this.$gridform[this.name].rows({rows})
                }
            },
            formdata() {
                return this.$store.getters[$.ns('formdata', this.name)]
            },
            autofocus() {
                return this.$store.getters[$.ns('autofocus', this.name)]
            },

            // handle submit button disabled state
            canSubmit() {
                if (!this.options.validate) return true
                return this.valid && !this.options.disabled
            },
            dialogWidth() {
                let width
                switch(this.$vuetify.breakpoint.name) {
                    case 'xs': width = '98%'; break
                    case 'sm': width = '80%'; break
                    case 'lg': width = '40%'; break
                    case 'xl': width = '40%'; break
                    default: width = '60%'; break
                }
                return width
            }
        },
        watch: {
            // when VGridform has v-model and options.keys are true
            // we bind keydown eventlistener for F4=submit and F10=cancel
            value: {
                immediate: true,
                handler(value) {
                    this.isActive = value
                    value ? this._bindKeys() : this._unbindKeys()
                }
            },
            permissions(perm) {
                if (perm && perm.microphone && perm.microphone.prompt) {
                    SPEECH.start({
                        onerror: (error) => {
                            this.dispatch(ATYPES.GFPERMISSION, {
                                microphone: { allowed: false, error, prompt: false }
                            })
                        }
                    })
                        .then((instance) => {
                            this.$gridform.speech = instance
                            this.dispatch(ATYPES.GFPERMISSION, {
                                microphone: { allowed: true, prompt: false }
                            })
                        })
                        .catch((error) => {
                            this.dispatch(ATYPES.GFPERMISSION, {
                                microphone: { allowed: false, error, prompt: false }
                            })
                        })
                }
            }
        },

        // NOTE when Hot Reloading the lifecycle is created -> destroyed -> mounted
        // register nested store module for this instance
        created() {
            this.registerStore()
        },
        // subscribe to APPLY_CHANGES and callback field changes
        // if defined, autofocus field
        mounted() {
            const {name} = this

            // listen to changes on form data
            // NOTE computed prop for the used getter doesn't work
            // https://github.com/vuejs/vue/issues/6518#issuecomment-437551411
            // immediate check to validate form data from defaults or store
            this.unwatchFormData = this.$watch('formdata', () => {
                const validation = this.$store.getters[$.ns('validation', this.name)]
                if (!validation) return

                // if all rules are valid, call custom callback for user defined validations
                const valid = validation.valid ? this.customCallbacks.beforeValidation($.formToStore(this.formdata)) : null
                // if custom callback has returned a boolean, we override the gridform validation result
                this.valid = typeof valid === 'boolean' ? valid : validation.valid
                this.customCallbacks.onValidAction(this.valid)
            }, {immediate: true})

            // listen to changes for this instance
            // callback onFieldChange with payload
            this.unsubscribeStore = this.$store.subscribe(action => {
                const {type, payload} = action
                if (type === $.ns(MTYPES.APPLY_CHANGES, name)) {
                    this.customCallbacks.onFieldChange(payload, this.context)
                }
                else if (type === $.ns(MTYPES.SET_FIELDLISTS_RATING, name)) {
                    this.customCallbacks.onFieldListChange()
                }
                else if (type === $.ns(MTYPES.DELETE_GRIDFORM)) {
                    const dname = _.get(payload, 'name')
                    this.$gridform.destroy(dname)
                }
            })

            if (this.autofocus && this.$autofocus) {
                this.$autofocus(`#${CONST.CLASS_FIELD}-${this.autofocus}`)
            }
        },
        // unsubscribe and unwatch listener
        beforeDestroy() {
            if (typeof this.unsubscribeStore === 'function') this.unsubscribeStore()
            if (typeof this.unwatchFormData === 'function') this.unwatchFormData()
        },
        destroyed() {
            if (_.get(this.options, 'destroy')) {
                const {name} = this
                // clear adapter here too, subscribe listener is already stopped
                this.$gridform.destroy(name)
                // drop gridform from store
                // unregister nested module from store
                void this.$store.dispatch($.ns(ATYPES.GFDELETE), {name})
            }
        },

        methods: {
            _bindKeys() {
                if (!_.get(this.options, 'keys')) return
                this.keyHandler = e => {
                    if (e.target.nodeName !== 'BODY' && e.target.getAttribute('role') !== 'document') return

                    const gf = this.$gridform[this.name]
                    if (e.code === 'F4') this.submit()
                    if (e.code === 'F10' || e.code === 'Escape') this.cancel()
                    // set focus to fieldlist
                    else if (e.code === 'ArrowUp' && !e.ctrlKey) gf.fieldlistFocus({command: 'prev'})
                    else if (e.code === 'ArrowDown' && !e.ctrlKey) gf.fieldlistFocus({command: 'next'})
                    else if (e.code === 'Home' && !e.ctrlKey) gf.fieldlistFocus({command: 'first'})
                    else if (e.code === 'End' && !e.ctrlKey) gf.fieldlistFocus({command: 'last'})
                    // set collapsed state for focused fieldlist
                    else if (e.code === 'ArrowLeft') gf.fieldlist({toggle: true, collapsed: true})
                    else if (e.code === 'ArrowRight') gf.fieldlist({toggle: true, collapsed: false})
                }
                document.addEventListener('keydown', this.keyHandler, true)
            },
            _unbindKeys() {
                if (typeof this.keyHandler !== 'function') return
                document.removeEventListener('keydown', this.keyHandler, true)
            },

            registerStore() {
                if (!this.$store) {
                    throw new Error('vuex store is required')
                }

                const {name} = this

                // prevent registering and dispatchs every time we choose a tab with the gridform
                // or after hot reloading (check module.hot?)
                const checkGetters = this.$store.getters[$.ns('formdata', name)]
                // NOTE this.$gridform[name] can exists, but module is unregistered and there are no actions and getters
                if (!this.$gridform[name] || _.isUndefined(checkGetters)) {
                    this.$gridform.registerNestedModule(name)
                    // dispatch to global store
                    const config = _.cloneDeep(this.config)
                    this.$store.dispatch($.ns(ATYPES.GFADD), {name, config}).then(() => {
                        this.$gridform.trigger(name, 'ready')
                    })
                }

                // NOTE always renew adapter cause this changes
                // bind gridform adapter to plugin instance
                // easy way for other components to use instance commands
                this.$gridform[name] = new GF(this)
            },
            /**
             * Dispatch Wrapper - check if gridform module is registered
             * @param {String} action - unprefixed action name
             * @param payload - action payload
             */
            dispatch(action, payload) {
                if (!action) return Promise.resolve()
                return this.$store.dispatch($.ns(action, this.name), payload)
            },

            submit() {
                if (!this.canSubmit) return

                this.validate()
                const fd = {}
                _.each(this.formdata, (field) => {
                    fd[field.name] = field.value
                })
                this.$emit('submit', fd)
                this.customCallbacks.onSubmitAction(fd, this.context)
            },
            cancel() {
                this.$emit('cancel')
                this.customCallbacks.onCancelAction(this.context)
            },
            validate() {
                const form = this.$refs.form
                if (!form) return

                form.validate()

                if (this.options.verboseValidation)
                    this.$toast.open({
                        title: 'Validation',
                        text: 'Form has ' + (this.valid ? 'no errors' : 'errors'),
                        type: this.valid ? 'success' : 'error',
                        timeout: 4000
                    })

                return this.valid
            },
            reset() {
                // NOTE form.reset() triggers update for every single field and sends "undefined" as value
                this.resetValidation()
                // dispatcher resets all fields to initial values
                this.dispatch(ATYPES.GFRESET, this.name)
            },
            resetValidation() {
                if (this.$refs.form) this.$refs.form.resetValidation()
            },

            hasAction(action) {
                const _actions = this.options.actions
                return Array.isArray(_actions) && _actions.includes(action)
            },
            emitAction(item) {
                // NOTE item is reactive, the callback can change it
                this.customCallbacks.onDrawerAction(item)
            }
        }
    }
</script>

<template>
    <VContainer v-if="options" :class="containerClasses">
        <VLayout :class="layoutClasses">
            <VFlex :class="mainClasses">
                <VSheet :class="sheetClasses" :dark="dark" :elevation="options.elevation">
                    <!--Sidebar slot="drawer"-->
                    <VNavigationDrawer
                        v-if="toolbarOptions.toolbarDrawer"
                        v-model="drawer"
                        v-bind="drawerOptions"
                        temporary>
                        <slot name="drawer">
                            <VFlex>
                                <VToolbar :dark="!dark">
                                    <VBtn text icon @click.stop="drawer = !drawer">
                                        <VIcon>mdi-keyboard-arrow-left</VIcon>
                                    </VBtn>
                                    <VToolbarTitle>{{ drawerOptions.drawerTitle }}</VToolbarTitle>
                                    <VSpacer></VSpacer>
                                    <VBtn text icon>
                                        <VIcon>mdi-more-vert</VIcon>
                                    </VBtn>
                                </VToolbar>
                                <VList v-if="options.drawerItems" dense>
                                    <template v-for="(item, i) in options.drawerItems">
                                        <VDivider
                                            v-if="item.subheader && i"
                                            :key="'d' + item.name"
                                            inset></VDivider>
                                        <VSubheader v-if="item.subheader" :key="'s' + item.name" inset>{{ item.subheader }}</VSubheader>
                                        <VListItem :key="item.name" @click.stop="emitAction(item)">
                                            <VListItemAvatar>
                                                <VIcon>{{ item.icon }}</VIcon>
                                            </VListItemAvatar>
                                            <VListItemContent>
                                                <VListItemTitle>{{ item.title }}</VListItemTitle>
                                            </VListItemContent>
                                        </VListItem>
                                    </template>
                                </VList>
                            </VFlex>
                        </slot>
                    </VNavigationDrawer>

                    <!--Toolbar-->
                    <VToolbar v-if="toolbarOptions.toolbar" v-bind="toolbarOptions.bind">
                        <!--            <VToolbarSideIcon v-if="toolbarOptions.toolbarDrawer" @click.stop="drawer = !drawer"></VToolbarSideIcon>-->
                        <VToolbarTitle>{{ toolbarOptions.toolbarTitle }}</VToolbarTitle>
                        <VSpacer></VSpacer>
                        <slot name="menu">
                            <VBtn v-if="toolbarOptions.toolbarMenu" text icon>
                                <VIcon>mdi-more-vert</VIcon>
                            </VBtn>
                        </slot>
                    </VToolbar>

                    <!--Formular-->
                    <VCard :id="`vgf-${name}`" class="vgf__card" :dark="dark" elevation="0">
                        <!--Header-->
                        <slot name="header">
                            <VCardTitle v-if="options.header">
                                <div class="vgf__title">{{ options.header }}</div>
                            </VCardTitle>
                        </slot>

                        <!--Content-->
                        <VCardText v-if="rows" :class="contentClasses" :style="{height: options.height, overflow: 'hidden'}">
                            <VForm ref="form" :lazy-validation="options.lazyValidation" @submit.prevent="submit">
                                <VGridformFieldList
                                    ref="gffl"
                                    :name="name"
                                    :list="fieldLists"
                                    :config="options"
                                    :rows="rows"
                                    :valid="valid"
                                    :callbacks="customCallbacks"
                                    :dev-tools="devTools"
                                    @submit="submit"/>
                                <!-- autoFooter -->
                                <VFooter
                                    v-if="options.autoFooter"
                                    color="transparent"
                                    class="caption font-weight-regular pt-0 px-6"
                                >{{ $vuetify.lang.t('$vuetify.gf.required') }}</VFooter>
                            </VForm>

                            <!--Default slot-->
                            <slot/>
                        </VCardText>

                        <!-- Empty slot -->
                        <slot v-if="!rows" name="empty"/>

                        <!--Buttons-->
                        <VCardActions v-if="options.actions && rows.length" class="vgf__actions" :class="{'vgf__actions--block': options.actions === true && actionOptions.block}">
                            <VSpacer/>
                            <VBtn v-if="options.actions === true" type="submit"
                                  :text="actionOptions.text"
                                  :block="actionOptions.block"
                                  :raised="actionOptions.raised"
                                  :color="actionOptions.color"
                                  :loading="options.loading"
                                  :disabled="!canSubmit"
                                  tabindex="100"
                                  @click.left="submit">
                                {{ options.actiontext }}
                            </VBtn>
                            <VBtn v-if="hasAction('cancel')" raised outlined tabindex="99" @click.left="cancel">
                                {{ $vuetify.lang.t('$vuetify.actions.cancel') }}
                            </VBtn>
                            <VBtn v-if="hasAction('submit')" type="submit" raised color="primary" tabindex="100" :disabled="!canSubmit" @click.left="submit">
                                {{ $vuetify.lang.t('$vuetify.actions.ok') }}
                            </VBtn>
                            <VBtn v-if="hasAction('save')" type="submit" raised color="primary" tabindex="100" :disabled="!canSubmit" @click.left="submit">
                                {{ $vuetify.lang.t('$vuetify.actions.save') }}
                            </VBtn>
                            <VBtn v-if="hasAction('apply')" type="submit" raised color="primary" tabindex="100" :disabled="!canSubmit" @click.left="submit">
                                {{ $vuetify.lang.t('$vuetify.actions.apply') }}
                            </VBtn>
                        </VCardActions>

                        <!--Slot: Below gridform actions-->
                        <slot name="belowActions"/>
                    </VCard>
                </VSheet>

                <!--Slot: Below gridform-->
                <slot name="below"/>
            </VFlex>
        </VLayout>
    </VContainer>
</template>

<style lang="scss" scoped>
    .vgf {
        position: relative;
        overflow: hidden; // hide navigation drawer
        border-radius: 0; // removes little edge when drawers open
    }

    .vgf__title {
        padding-left: 12px;
        font-size: 24px !important;
        font-weight: 700;
        line-height: 32px !important;
        letter-spacing: normal !important;
    }

    .vgf__content {
        padding: 0;
    }

    .vgf__actions.vgf__actions--block {
        padding-right: 24px;
        padding-left: 24px;

        .v-btn.v-btn--block.theme--light.accent:hover {
            background-color: #000000 !important;
        }
    }
</style>
